export const DEFAULT_AUTH_DESTINATION = "/create-clips";
export const AUTH_REDIRECT_MAX_AGE_MS = 10 * 60 * 1000;

const AUTH_REDIRECT_PATH_KEY = "redirectAfterAuth";
const AUTH_REDIRECT_CREATED_AT_KEY = "redirectAfterAuthCreatedAt";
const MAX_DESTINATION_LENGTH = 256;
const UNSAFE_TEXT = /[\u0000-\u001f\u007f-\u009f\\]/;
const SAFE_JOB_ID = /^[A-Za-z0-9_-]{1,128}$/;

const normalizedExactDestination = (pathname: string): string | null => {
  const normalized = pathname.endsWith("/") && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;

  switch (normalized.toLowerCase()) {
    case "/create-clips":
      return DEFAULT_AUTH_DESTINATION;
    case "/dashboard":
      return "/dashboard";
    case "/clips":
      return "/clips";
    default:
      return null;
  }
};

export const sanitizeAuthDestination = (
  candidate: unknown
): string | null => {
  if (
    typeof candidate !== "string" ||
    candidate.length === 0 ||
    candidate.length > MAX_DESTINATION_LENGTH ||
    candidate !== candidate.trim() ||
    !candidate.startsWith("/") ||
    candidate.startsWith("//") ||
    candidate.includes("?") ||
    candidate.includes("#") ||
    UNSAFE_TEXT.test(candidate)
  ) {
    return null;
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(candidate);
  } catch {
    return null;
  }

  if (
    decoded.startsWith("//") ||
    decoded.includes("?") ||
    decoded.includes("#") ||
    UNSAFE_TEXT.test(decoded)
  ) {
    return null;
  }

  const exactDestination = normalizedExactDestination(decoded);
  if (exactDestination) return exactDestination;

  const jobMatch = decoded.match(/^\/create-clips\/([^/]+)\/?$/i);
  if (!jobMatch || !SAFE_JOB_ID.test(jobMatch[1])) return null;
  return `/create-clips/${encodeURIComponent(jobMatch[1])}`;
};

export const clearAuthRedirectDestination = (): void => {
  try {
    sessionStorage.removeItem(AUTH_REDIRECT_PATH_KEY);
    sessionStorage.removeItem(AUTH_REDIRECT_CREATED_AT_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
};

export const storeAuthRedirectDestination = (
  candidate: unknown,
  nowMilliseconds = Date.now()
): string => {
  const destination =
    sanitizeAuthDestination(candidate) ?? DEFAULT_AUTH_DESTINATION;

  try {
    sessionStorage.setItem(AUTH_REDIRECT_PATH_KEY, destination);
    sessionStorage.setItem(
      AUTH_REDIRECT_CREATED_AT_KEY,
      String(nowMilliseconds)
    );
  } catch {
    // The in-memory return value still gives the caller a safe destination.
  }

  return destination;
};

export const readAuthRedirectDestination = (
  nowMilliseconds = Date.now()
): string | null => {
  try {
    const destination = sanitizeAuthDestination(
      sessionStorage.getItem(AUTH_REDIRECT_PATH_KEY)
    );
    const createdAt = Number(
      sessionStorage.getItem(AUTH_REDIRECT_CREATED_AT_KEY)
    );
    const age = nowMilliseconds - createdAt;

    if (
      !destination ||
      !Number.isFinite(createdAt) ||
      createdAt <= 0 ||
      age < 0 ||
      age > AUTH_REDIRECT_MAX_AGE_MS
    ) {
      clearAuthRedirectDestination();
      return null;
    }

    return destination;
  } catch {
    return null;
  }
};

export const consumeAuthRedirectDestination = (
  nowMilliseconds = Date.now()
): string | null => {
  const destination = readAuthRedirectDestination(nowMilliseconds);
  clearAuthRedirectDestination();
  return destination;
};

export const signInPathForDestination = (candidate: unknown): string => {
  const destination =
    sanitizeAuthDestination(candidate) ?? DEFAULT_AUTH_DESTINATION;
  return `/sign-in?next=${encodeURIComponent(destination)}`;
};

const oauthParameters = (value: string): URLSearchParams => {
  const prefixLength = value.startsWith("?") || value.startsWith("#") ? 1 : 0;
  return new URLSearchParams(value.slice(prefixLength));
};

export const oauthErrorMessage = (
  search: string,
  hash: string
): string | null => {
  const parameterSets = [oauthParameters(search), oauthParameters(hash)];
  const errorCode = parameterSets
    .map((parameters) => parameters.get("error"))
    .find((value): value is string => Boolean(value));

  if (!errorCode) return null;
  if (errorCode === "access_denied") {
    return "Google sign-in was canceled. You can retry or return home.";
  }
  return "Google sign-in could not be completed. Please retry.";
};
